import {
  DEFAULT_ESCALATION_EMAIL_SETTINGS,
  MANAGE_ESCALATIONS_PERMISSION,
  SETTINGS_ESCALATIONS_PERMISSION,
  countConditions,
  type EscalationCounts,
  type EscalationDetail,
  type EscalationEmailPreview,
  type EscalationEmailSendResult,
  type EscalationEmailSettings,
  type EscalationMailProblem,
  type EscalationStatusNowResult,
  type EscalationRow,
  type EscalationsRepository,
  type ListQuery,
  type Page,
  type WriteResult,
} from "@estelle/domain";

import { ForbiddenError, apiFetch } from "@/adapters/api/base";

/**
 * Delivery escalations over HTTP (#702).
 *
 * `apps/api/src/routes/escalations.ts` is the other half of this file. A
 * refusal the server gives for an expected reason (a stop-tracking without a
 * comment, a row already closed) arrives as a `WriteResult` body with a 4xx
 * status, a rejection to show; a 403 from the permission guard is a `forbidden`
 * rejection on a write and `ForbiddenError` on a read. Only a transport failure
 * throws, with the status and path and never a body.
 *
 * Nothing is written locally: every answer is the server's, and a screen
 * replaces what it shows from it.
 */

const BASE = "/api/escalations";

function params(query: ListQuery | undefined): URLSearchParams {
  const out = new URLSearchParams();
  if (!query) return out;
  if (query.limit !== undefined) out.set("limit", String(query.limit));
  if (query.cursor !== undefined) out.set("cursor", query.cursor);
  if (query.search !== undefined && query.search !== "") {
    out.set("search", query.search);
  }
  if (query.filter && countConditions(query.filter) > 0) {
    out.set("filter", JSON.stringify(query.filter));
  }
  if (query.sort && query.sort.length > 0) {
    out.set(
      "sort",
      query.sort.map((entry) => `${entry.column}:${entry.direction}`).join(","),
    );
  }
  return out;
}

async function read<T>(path: string, query?: ListQuery): Promise<T | null> {
  const search = params(query).toString();
  const response = await apiFetch(
    `${BASE}${path}${search ? `?${search}` : ""}`,
  );
  if (response.status === 404) return null;
  if (response.status === 403) {
    const body = (await response.json().catch(() => null)) as {
      permission?: unknown;
    } | null;
    throw new ForbiddenError(
      `${BASE}${path}`,
      typeof body?.permission === "string" ? body.permission : undefined,
    );
  }
  if (!response.ok) {
    throw new Error(
      `Escalations request failed: ${String(response.status)} on ${path}`,
    );
  }
  return (await response.json()) as T;
}

async function write<T>(
  path: string,
  body: unknown,
  method: "POST" | "PUT" = "POST",
  permission: string = MANAGE_ESCALATIONS_PERMISSION,
): Promise<WriteResult<T>> {
  const response = await apiFetch(`${BASE}${path}`, {
    method,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const result = (await response.json().catch(() => null)) as
    WriteResult<T> | { permission?: unknown } | null;
  if (result !== null && typeof result === "object" && "ok" in result) {
    return result;
  }
  if (response.status === 403) {
    return {
      ok: false,
      rejection: {
        kind: "forbidden",
        message: "You do not have permission to do that.",
        permission:
          result !== null && typeof result.permission === "string"
            ? result.permission
            : permission,
      },
    };
  }
  throw new Error(
    `Escalations ${method} failed: ${String(response.status)} on ${path}`,
  );
}

export function createApiEscalations(): EscalationsRepository {
  return {
    list: async (query) =>
      (await read<Page<EscalationRow>>("", query)) ?? {
        items: [],
        nextCursor: null,
        total: 0,
      },
    counts: async () =>
      (await read<EscalationCounts>("/counts")) ?? {
        new: 0,
        sent: 0,
        resolved: 0,
        dismissed: 0,
      },
    get: (id) => read<EscalationDetail>(`/${encodeURIComponent(id)}`),
    markSent: (ids) => write("/mark-sent", { ids }),
    dismiss: (id, input) => write(`/${encodeURIComponent(id)}/dismiss`, input),
    reopen: (id) => write(`/${encodeURIComponent(id)}/reopen`, {}),
    emailPreview: (ids) =>
      write<EscalationEmailPreview>("/email-preview", { ids }),
    emailCouriers: (ids) =>
      write<EscalationEmailSendResult>("/email-couriers", { ids }),
    emailStatusNow: () => write<EscalationStatusNowResult>("/status-now", {}),
    emailSettings: async () =>
      (await read<EscalationEmailSettings>("/email-settings")) ??
      DEFAULT_ESCALATION_EMAIL_SETTINGS,
    saveEmailSettings: (patch) =>
      write<EscalationEmailSettings>(
        "/email-settings",
        patch,
        "PUT",
        SETTINGS_ESCALATIONS_PERMISSION,
      ),
    mailProblems: async () =>
      (await read<EscalationMailProblem[]>("/mail-problems")) ?? [],
  };
}
